'use client';
import React, { useState } from 'react';

import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import PageHeader from '@/components/shared/PageHeader';
import FilterRow from '@/components/shared/FilterRow';
import EmptyState from '@/components/shared/EmptyState';
import { Skeleton } from '@/components/shared/Skeletons';
import { localsGuideSections, LocalsGuideSection, LocalsGuideEntry } from '@/data/mockLocalsGuide';
import { MapPin, BookOpen } from 'lucide-react';

const sectionFilters = [
  { label: 'All', value: 'all' },
  ...localsGuideSections.map((s) => ({ label: s.title, value: s.id })),
];

function EntrySkeleton() {
  return (
    <div className="neon-card p-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-24 rounded-full" />
        </div>
        <Skeleton className="h-5 w-12 rounded-full" />
      </div>
      <Skeleton className="h-3 w-full mb-1.5" />
      <Skeleton className="h-3 w-4/5 mb-3" />
      <Skeleton className="h-3 w-3/5" />
    </div>
  );
}

function EntryCard({ entry, color }: { entry: LocalsGuideEntry; color: string }) {
  if (entry.status === 'removed') {
    return (
      <div className="neon-card p-5 opacity-50">
        <p className="text-[13px]" style={{ color: '#A7A3C4' }}>This resource is no longer available.</p>
      </div>
    );
  }

  return (
    <div className="neon-card p-5 h-full">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-display font-semibold text-[14px] leading-snug" style={{ color: '#F2F4FF' }}>
          {entry.name}
        </h4>
        {entry.priceRange && (
          <span
            className="text-[11px] px-2 py-0.5 rounded font-medium flex-shrink-0"
            style={{ background: `${color}10`, color, border: `1px solid ${color}20` }}
          >
            {entry.priceRange}
          </span>
        )}
      </div>

      {entry.subcategory && (
        <p className="text-[11px] font-medium mb-2 uppercase tracking-wide" style={{ color: 'rgba(167,163,196,0.50)' }}>
          {entry.subcategory}
          {entry.distance && ` · ${entry.distance}`}
        </p>
      )}

      <p className="text-[13px] leading-relaxed mb-3" style={{ color: '#A7A3C4' }}>
        {entry.description}
      </p>

      {entry.insiderNote && (
        <div
          className="px-3 py-2.5 rounded-lg mb-3"
          style={{ background: `${color}08`, border: `1px solid ${color}18` }}
        >
          <p className="text-[12px] leading-relaxed" style={{ color }}>
            <span className="font-semibold">Insider: </span>{entry.insiderNote}
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {entry.tags.slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="text-[11px] px-2 py-0.5 rounded"
            style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(167,163,196,0.60)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

function SectionBlock({ section }: { section: LocalsGuideSection }) {
  return (
    <div id={section.id}>
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-[20px] flex-shrink-0"
          style={{ background: `${section.color}12`, border: `1px solid ${section.color}22` }}
        >
          {section.icon}
        </div>
        <div>
          <h2 className="font-display font-bold text-[20px]" style={{ color: '#F2F4FF' }}>
            {section.title}
          </h2>
          <p className="text-[13px]" style={{ color: '#A7A3C4' }}>{section.description}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-2">
        {section.entries.map((entry) => (
          <EntryCard key={entry.id} entry={entry} color={section.color} />
        ))}
      </div>
    </div>
  );
}

export default function LocalsGuidePage() {
  const [activeSection, setActiveSection] = useState('all');
  const [isLoading] = useState(false);

  const visibleSections = activeSection === 'all'
    ? localsGuideSections
    : localsGuideSections.filter((s) => s.id === activeSection);

  const totalEntries = localsGuideSections.reduce((acc, s) => acc + s.entries.length, 0);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <PageHeader
          eyebrow="Resident Resource"
          title="Locals Guide"
          description="A curated insider guide to the best of Las Vegas — through the lens of a Panorama resident. Not a tourist list. A resident's list."
          stat={{ value: String(totalEntries), label: 'recommendations' }}
        />

        {/* Section nav */}
        <div style={{ background: '#0C0A16', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-4 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {localsGuideSections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all duration-150 hover:opacity-80"
                  style={{ background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.07)' }}
                >
                  <span>{s.icon}</span>
                  <span>{s.title}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          {/* Filter */}
          <div className="mb-8">
            <FilterRow filters={sectionFilters} active={activeSection} onChange={setActiveSection} />
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 9 }).map((_, i) => <EntrySkeleton key={i} />)}
            </div>
          )}

          {/* Empty */}
          {!isLoading && visibleSections.length === 0 && (
            <EmptyState
              title="No entries found"
              description="Try a different section filter."
              icon={<MapPin size={22} style={{ color: '#4CC9FF' }} />}
              action={
                <button className="btn-secondary text-[13px] px-4 py-2" onClick={() => setActiveSection('all')}>
                  Show all sections
                </button>
              }
            />
          )}

          {/* Content */}
          {!isLoading && visibleSections.length > 0 && (
            <div className="space-y-14">
              {visibleSections.map((section, idx) => (
                <div key={section.id}>
                  <SectionBlock section={section} />
                  {idx < visibleSections.length - 1 && (
                    <div className="neon-divider mt-14" />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Footer note */}
          <div
            className="mt-14 px-5 py-4 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="flex items-start gap-3">
              <BookOpen size={16} style={{ color: 'rgba(167,163,196,0.40)', marginTop: 2, flexShrink: 0 }} />
              <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.50)' }}>
                This guide is curated by and for Panorama residents. Recommendations are based on resident experience and are not sponsored unless noted. Example partner slots are placeholders for future verified partnerships. Always verify current hours, availability, and pricing directly.
              </p>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
